import type {
  CrossSessionLink,
  Finding,
  FindingType,
  KillChainNarrative,
  KillChainPhase,
  KillChainPhaseInfo,
  LogEntry,
  NarrativeEvent,
  SeverityLevel,
} from './insightDashboardTypes';

/**
 * Parses raw pipe-delimited log text into an array of LogEntry objects.
 * @param text - The raw log text to parse.
 * @returns Array of parsed log entries.
 */
export function parseLogLines(text: string): LogEntry[] {
  const lines = text.split('\n');

  return lines.slice(1).reduce<LogEntry[]>((entries, line) => {
    const parts = line.split('|');

    if (parts.length >= 27) {
      entries.push({
        lineNumber: parseInt(parts[0], 10),
        logId: parts[0],
        requestId: parts[1],
        sessionId: parts[2],
        logType: parts[4],
        startTimeUtc: parts[5],
        endTimeUtc: parts[6],
        severity: parts[15],
        auditClass: parts[16],
        tcodeDeviation: parts[18],
        transactionCode: parts[19],
        instance: parts[20],
        logCreationDateTime: parts[22],
        terminal: parts[23],
        programName: parts[24],
        client: parts[25],
        details: parts[26],
      });
    }

    return entries;
  }, []);
}

/**
 * Filters log entries by line number.
 * @param allEntries - All available log entries.
 * @param lineNumbers - Line numbers to filter by.
 * @returns Matching log entries.
 */
export function getLogEntriesByLines(
  allEntries: LogEntry[],
  lineNumbers: number[]
): LogEntry[] {
  return allEntries.filter((entry) => lineNumbers.includes(entry.lineNumber));
}

/**
 * Returns unique session IDs associated with a finding's evidence log IDs.
 * @param finding - The finding to inspect.
 * @param logEntries - All log entries to search.
 * @returns Array of unique session ID strings.
 */
export function getSessionIdsForFinding(
  finding: Finding,
  logEntries: LogEntry[]
): string[] {
  if (!finding.evidenceLogIds?.length) {
    return [];
  }

  const sessionSet = new Set<string>();

  finding.evidenceLogIds.forEach((logId) => {
    const entry = logEntries.find((e) => e.logId === logId);

    if (entry) {
      sessionSet.add(entry.sessionId);
    }
  });

  return Array.from(sessionSet);
}

/**
 * Builds cross-session links from findings that span multiple sessions.
 * @param allFindings - All findings to inspect.
 * @param logEntries - All log entries.
 * @returns Array of cross-session link objects.
 */
export function getCrossSessionLinks(
  allFindings: Finding[],
  logEntries: LogEntry[]
): CrossSessionLink[] {
  return allFindings.reduce<CrossSessionLink[]>((links, f) => {
    const sessions = getSessionIdsForFinding(f, logEntries);

    if (sessions.length > 1) {
      links.push({
        sessionIds: sessions,
        severity: f.severity,
        findingId: f.id,
      });
    }

    return links;
  }, []);
}

/**
 * Formats a UTC timestamp string into a human-readable date-time string.
 * @param ts - Timestamp string in YYYYMMDDHHmmss format.
 * @returns Formatted string.
 */
export function formatTimestamp(ts: string): string {
  if (!ts || ts.length < 14) {
    return ts;
  }

  const y = ts.slice(0, 4);
  const m = ts.slice(4, 6);
  const d = ts.slice(6, 8);
  const h = ts.slice(8, 10);
  const mi = ts.slice(10, 12);
  const s = ts.slice(12, 14);

  return `${y}-${m}-${d} ${h}:${mi}:${s}`;
}

/**
 * Maps a severity level to a CSS color class name.
 * @param severity - The severity level string.
 * @returns Color class name string.
 */
export function getSeverityColor(severity: SeverityLevel | string): string {
  switch (severity.toUpperCase()) {
    case 'CRITICAL':
      return 'critical';
    case 'HIGH':
      return 'high';
    case 'MEDIUM':
      return 'medium';
    case 'LOW':
      return 'low';
    default:
      return 'low';
  }
}

/**
 * Converts a FindingType enum value to a human-readable label.
 * @param type - The finding type.
 * @returns Human-readable label.
 */
export function getTypeLabel(type: FindingType): string {
  return type
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Returns the display label for a kill chain phase.
 * @param phase - The kill chain phase.
 * @returns Display label string.
 */
export function getPhaseLabel(phase: KillChainPhase): string {
  const labels: Record<KillChainPhase, string> = {
    reconnaissance: 'Recon',
    weaponization: 'Weaponize',
    exploitation: 'Exploit',
    anti_forensics: 'Anti-Forensics',
  };

  return labels[phase];
}

/**
 * Returns the short display label for a kill chain phase.
 * @param phase - The kill chain phase.
 * @returns Short label string.
 */
export function getPhaseShortLabel(phase: KillChainPhase): string {
  const labels: Record<KillChainPhase, string> = {
    reconnaissance: 'RECON',
    weaponization: 'WEAPONIZE',
    exploitation: 'EXPLOIT',
    anti_forensics: 'ANTI-FORENSICS',
  };

  return labels[phase];
}

/**
 * Parses a full UTC timestamp string into a Unix epoch in seconds.
 * @param ts - Timestamp string in YYYYMMDDHHmmss format.
 * @returns Unix epoch seconds, or 0 if invalid.
 */
export function parseFullTimestamp(ts: string): number {
  if (!ts || ts.length < 14) {
    return 0;
  }

  const y = parseInt(ts.slice(0, 4), 10);
  const mo = parseInt(ts.slice(4, 6), 10) - 1;
  const d = parseInt(ts.slice(6, 8), 10);
  const h = parseInt(ts.slice(8, 10), 10);
  const m = parseInt(ts.slice(10, 12), 10);
  const s = parseInt(ts.slice(12, 14), 10);

  return new Date(y, mo, d, h, m, s).getTime() / 1000;
}

/**
 * Formats an epoch second value into a tick label based on the time range.
 * @param epochSec - Unix epoch in seconds.
 * @param rangeMinutes - Total range in minutes for context.
 * @returns Formatted tick label string.
 */
export function formatTickLabel(
  epochSec: number,
  rangeMinutes: number
): string {
  const d = new Date(epochSec * 1000);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');

  if (rangeMinutes > 1440) {
    const mon = d.toLocaleString('en', { month: 'short' });

    return `${mon} ${d.getDate()} ${hh}:${mm}`;
  }

  if (rangeMinutes > 60) {
    return `${hh}:${mm}`;
  }

  return `${hh}:${mm}:${ss}`;
}

/* ------------------------------------------------------------------ */
/*  Kill Chain Narrative builder                                       */
/* ------------------------------------------------------------------ */

function resolveStepEpoch(
  step: { timestamp?: string; sessionId?: string },
  logEntries: LogEntry[]
): number {
  if (!step.timestamp) {
    return 0;
  }

  const parts = step.timestamp.split(':');

  if (parts.length < 3) {
    return 0;
  }

  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const s = parseInt(parts[2], 10);

  const refEntry = logEntries.find(
    (e) =>
      e.sessionId === step.sessionId && parseFullTimestamp(e.startTimeUtc) > 0
  );

  if (!refEntry) {
    return 0;
  }

  const baseEpoch = parseFullTimestamp(refEntry.startTimeUtc);
  const baseDate = new Date(baseEpoch * 1000);
  const baseDayStart =
    new Date(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      baseDate.getDate()
    ).getTime() / 1000;

  return baseDayStart + h * 3600 + m * 60 + s;
}

const PHASE_ORDER: KillChainPhase[] = [
  'reconnaissance',
  'weaponization',
  'exploitation',
  'anti_forensics',
];

/**
 * Builds a structured kill chain narrative from findings, log entries, and timeline info.
 * @param findings - All findings.
 * @param logEntries - All log entries.
 * @param killChainTimeline - Kill chain phase info array.
 * @returns Structured KillChainNarrative object.
 */
export function buildKillChainNarrative(
  findings: Finding[],
  logEntries: LogEntry[],
  killChainTimeline: KillChainPhaseInfo[]
): KillChainNarrative {
  const sessionSet = new Set<string>();
  const phaseNarratives = PHASE_ORDER.map((phaseKey) => {
    const info = killChainTimeline.find((k) => k.phase === phaseKey) ?? {
      phase: phaseKey,
      label: getPhaseLabel(phaseKey),
      timeRange: '',
      summary: '',
    };

    const phaseFindings = findings.filter((f) => f.phase === phaseKey);
    const events: NarrativeEvent[] = [];

    phaseFindings.forEach((finding) => {
      finding.steps.forEach((step, stepIdx) => {
        const epoch = resolveStepEpoch(step, logEntries);
        const sid = step.sessionId ?? '';

        if (sid) {
          sessionSet.add(sid);
        }

        const prevStep = stepIdx > 0 ? finding.steps[stepIdx - 1] : null;
        const isCrossSessionPivot =
          prevStep?.sessionId !== undefined &&
          step.sessionId !== undefined &&
          prevStep.sessionId !== step.sessionId;

        events.push({
          finding,
          step,
          stepIndex: stepIdx,
          epoch,
          sessionId: sid,
          isCrossSessionPivot,
        });
      });
    });

    events.sort((a, b) => a.epoch - b.epoch);

    let durationSeconds = 0;

    if (events.length > 1) {
      const firstEpoch = events[0].epoch;
      const lastEpoch = events[events.length - 1].epoch;

      durationSeconds = lastEpoch > firstEpoch ? lastEpoch - firstEpoch : 0;
    }

    return {
      phase: phaseKey,
      info,
      events,
      findings: phaseFindings,
      durationSeconds,
      gapToNextSeconds: null as number | null,
    };
  });

  phaseNarratives.slice(0, -1).forEach((current, i) => {
    const next = phaseNarratives[i + 1];
    const currentLast = current.events.at(-1);

    if (
      currentLast !== undefined &&
      next.events.length > 0 &&
      next.events[0].epoch > currentLast.epoch
    ) {
      current.gapToNextSeconds = next.events[0].epoch - currentLast.epoch;
    }
  });

  const allEpochs = phaseNarratives
    .flatMap((p) => p.events.map((e) => e.epoch))
    .filter((e) => e > 0);

  const totalDurationSeconds =
    allEpochs.length > 1 ? Math.max(...allEpochs) - Math.min(...allEpochs) : 0;

  return {
    phases: phaseNarratives,
    sessions: Array.from(sessionSet).sort(),
    totalDurationSeconds,
  };
}
